import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import { i18nLinks, i18nOgMeta, localeUrl } from "@/lib/seo";
import { seasonsQueryOptions } from "@/tools/f1-fantasy/hooks/use-f1-data";
import { statisticsLeaderboardQueryOptions } from "@/tools/f1-fantasy/hooks/use-statistics";
import { DEFAULT_SEASON } from "@/tools/f1-fantasy/lib/constants";
import { Statistics } from "@/tools/f1-fantasy/pages/Statistics";

// `tab` / `entity` have NO schema defaults on purpose: a default is written
// back into the URL, which made the bare /f1-fantasy/statistics 307 to
// ?tab=race_results&entity=drivers while its canonical is the bare URL — a
// canonical pointing at a redirect. The page applies the defaults itself.
const statisticsSearchSchema = z.object({
  tab: z.enum(["race_results", "fantasy_stats"]).optional().catch(undefined),
  entity: z.enum(["drivers", "constructors"]).optional().catch(undefined),
  season: z.coerce.number().optional(),
  // Sort persistence — shareable / refresh-safe sort state
  sort: z.string().optional(),
  dir: z.enum(["asc", "desc"]).optional(),
});

export const Route = createFileRoute("/f1-fantasy/statistics/")({
  head: () => ({
    meta: [
      { title: "F1 Driver & Constructor Statistics | Toolverse" },
      {
        name: "description",
        content:
          "Full F1 driver and constructor statistics leaderboard across all seasons. Race results, fantasy points per race, PPM ratios, and season-long performance.",
      },
      {
        property: "og:title",
        content: "F1 Driver & Constructor Statistics | Toolverse",
      },
      ...i18nOgMeta("/f1-fantasy/statistics"),
    ],
    links: [...i18nLinks("/f1-fantasy/statistics")],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: localeUrl("/"),
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "F1 Fantasy",
              item: localeUrl("/f1-fantasy"),
            },
            {
              "@type": "ListItem",
              position: 3,
              name: "Statistics",
              item: localeUrl("/f1-fantasy/statistics"),
            },
          ],
        }),
      },
    ],
  }),
  component: Statistics,
  validateSearch: statisticsSearchSchema,
  ssr: true,
  loaderDeps: ({ search }) => ({ season: search.season }),
  loader: async ({ context, deps }) => {
    const seasons = await context.queryClient.ensureQueryData(
      seasonsQueryOptions(),
    );
    const latestSeason = seasons?.[0]?.year ?? DEFAULT_SEASON;
    const season = deps.season ?? latestSeason;

    // Ensure default leaderboard is loaded before rendering to avoid hydration mismatch
    await context.queryClient.ensureQueryData(
      statisticsLeaderboardQueryOptions("race_results", "drivers", season),
    );

    return { season };
  },
});
